import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import React, { SetStateAction, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { formSchema, InviteStaffFormValues } from "./inviteStaffForm.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { inviteHiringStaff } from "@endpoints/hiringCompany/hiringCompany";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import { HiringCompanyType } from "@utils/constants";

type InviteMemberDialogProps = {
  isInviteDialogOpen: boolean;
  setIsInviteDialogOpen: React.Dispatch<SetStateAction<boolean>>;
  onInviteSuccess?: () => void;
};

const InviteMemberDialog = (props: InviteMemberDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isInviteDialogOpen, setIsInviteDialogOpen, onInviteSuccess } = props;
  const { user } = useSelector((state: RootState) => state.auth);
  const isHiringManager = user?.hiringCompanyType === HiringCompanyType.MANAGER;

  const { control, handleSubmit, setValue, watch } =
    useForm<InviteStaffFormValues>({
      resolver: zodResolver(formSchema) as any,
      defaultValues: {
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        jobTitle: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        country: "",
        postCode: "",
        role: HiringCompanyType.TEAM_MEMBER,
      },
    });
  const [isInviting, setIsInviting] = useState(false);
  const fields = watch();

  useEffect(() => {
    if (user?.companyName) {
      setValue("company", user.companyName);
    }
  }, [user?.companyName, setValue]);

  const handleSave = async (data: any) => {
    try {
      setIsInviting(true);
      const payload = {
        ...data,
        role: isHiringManager ? HiringCompanyType.TEAM_MEMBER : data.role,
      };
      const { success } = await inviteHiringStaff(payload);
      if (success) {
        setIsInviteDialogOpen(false);
        enqueueSnackbar("Invitation sent successfully.", {
          variant: "success",
        });
        onInviteSuccess?.();
      }
    } catch (error) {
      enqueueSnackbar(error || "Something went wrong", {
        variant: "error",
      });
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
      <DialogContent className="bg-white w-[720px] max-w-[720px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription className="font-medium text-xs text-light-black">
            Send invitations for staff onboarding.
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-5 my-1">
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-4">
              <Controller
                name="firstName"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">First name *</span>
                    <Input
                      {...field}
                      placeholder=""
                      className="!text-xs"
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Last name *</span>
                    <Input
                      {...field}
                      placeholder=""
                      className="!text-xs"
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Company</span>
                    <Input
                      {...field}
                      placeholder=""
                      className="!text-xs"
                      disabled
                    />
                  </div>
                )}
              />
              <Controller
                name="jobTitle"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Job Title</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                  </div>
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Phone</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                  </div>
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">
                      Email address *
                    </span>
                    <Input
                      {...field}
                      placeholder=""
                      className="!text-xs"
                    />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col gap-4">
              <Controller
                name="address1"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Address 1 *</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="address2"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Address 2</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                  </div>
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">City</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                  </div>
                )}
              />
              <Controller
                name="country"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Country *</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                    {fieldState.error && (
                      <p className="text-red-500 text-xs">
                        {fieldState.error.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="postCode"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <span className="font-medium text-xs">Postcode</span>
                    <Input {...field} placeholder="" className="!text-xs" />
                  </div>
                )}
              />
            </div>
          </div>

          <div className="pt-2">
            <p className="text-sm font-semibold text-[#2E2E2E] mb-2">
              User Role
            </p>
            <label className="text-xs font-medium text-[#2E2E2E]">
              User Role *
            </label>
            <select
              name="role"
              value={
                isHiringManager
                  ? HiringCompanyType.TEAM_MEMBER
                  : (fields?.role ?? HiringCompanyType.TEAM_MEMBER)
              }
              disabled={isHiringManager}
              onChange={(e) => setValue("role", Number(e.target.value))}
              className="mt-2 h-10 w-full rounded-md border border-[#E6E6E6] bg-white px-3 text-xs font-medium text-[#2E2E2E]"
            >
              {[
                { label: "Admin", value: HiringCompanyType.ADMIN },
                { label: "Manager", value: HiringCompanyType.MANAGER },
                {
                  label: "Hiring team member",
                  value: HiringCompanyType.TEAM_MEMBER,
                },
              ]
                .filter((option) =>
                  isHiringManager
                    ? option.value === HiringCompanyType.TEAM_MEMBER
                    : true,
                )
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
            {isHiringManager ? (
              <p className="text-[11px] text-[#6F6F6F] mt-2">
                Hiring managers can only invite hiring team members.
              </p>
            ) : null}
          </div>
        </form>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleSubmit(handleSave)}
            type="submit"
            className="text-white gap-3"
          >
            Save
            {isInviting ? <LoadingAnimation className="w-4 h-4" /> : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteMemberDialog;
